// DeleteUserButton — trash button + inline two-step confirmation for one user row. The admin must
// type the user's code before Delete enables. The API is the authority: it refuses admins, yourself
// and any account with records, and returns the message shown here.
import { useState } from "react";
import { deleteJson } from "../../hooks/useApi.js";

interface DeleteUserButtonProps {
  id:        string;
  code:      string;
  onDeleted: (id: string) => void;
}

export default function DeleteUserButton({ id, code, onDeleted }: DeleteUserButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = typed.trim().toUpperCase() === code.toUpperCase();

  function cancel() {
    setConfirming(false);
    setTyped("");
    setError(null);
  }

  async function confirm() {
    if (!matches || isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      const res = await deleteJson<{ id: string }>(`/api/users/${id}?confirmCode=${encodeURIComponent(typed.trim())}`);
      if (!res.ok) {
        setError(res.error);
        setIsDeleting(false);
        return;
      }
      onDeleted(id);   // parent refetches, which drops the row and unmounts this component
    } catch {
      setError("Unable to delete user. Please try again.");
      setIsDeleting(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        aria-label={`Delete ${code}`}
        title="Delete"
        onClick={() => { setConfirming(true); }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-red-300 text-red-600 transition hover:bg-red-50"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
        </svg>
      </button>
    );
  }

  return (
    <form
      role="alert"
      onSubmit={(event) => { event.preventDefault(); void confirm(); }}
      className="max-w-xs rounded-md border border-red-200 bg-red-50 p-3 text-sm"
    >
      <label htmlFor={`delete-${id}`} className="block text-dm-navy">
        Delete this user? This cannot be undone. Type <span className="font-semibold">{code}</span> to confirm.
      </label>
      <input
        id={`delete-${id}`}
        type="text"
        autoComplete="off"
        value={typed}
        onChange={(event) => { setTyped(event.target.value); }}
        className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
      />
      {error && <p className="mt-2 text-red-600">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="submit"
          disabled={!matches || isDeleting}
          className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
        <button
          type="button"
          disabled={isDeleting}
          onClick={cancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-dm-slate transition hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
